// src/components/partners-hf.js

// The URL for the HF feature-extraction pipeline
const HF_URL =
  'https://api-inference.huggingface.co/pipeline/feature-extraction/sentence-transformers/all-MiniLM-L6-v2'

let hfToken = null

/**
 * Initialize the HF client with your token.
 * Call this once (e.g. in a useEffect in Partners.jsx).
 * @param {string} token — Your hf_… token
 */
export function initHfClient(token) {
  hfToken = token
}

/**
 * Embed a string by calling HF's feature-extraction endpoint
 * and mean-pooling the token vectors.
 * @param {string} text
 * @returns {Promise<number[]>} — embedding vector
 */
export async function embedText(text) {
  if (!hfToken) {
    throw new Error('Hugging Face token not initialized')
  }
  const res = await fetch(HF_URL, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${hfToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(text),
  })
  if (!res.ok) {
    const err = await res.text()
    throw new Error(`HF feature-extraction error: ${err}`)
  }
  const data = await res.json()  // Array of [dim]-length arrays, one per token
  // Mean-pool across tokens
  const dims = data[0].length
  const sum = Array(dims).fill(0)
  data.forEach(tok => {
    for (let i = 0; i < dims; i++) {
      sum[i] += tok[i]
    }
  })
  return sum.map(v => v / data.length)
}

/**
 * Ensure every profile has an embedding, reusing existing vectors.
 * @param {Array<Object>} profiles — each with .id, .skill, .tags
 * @param {Object<string, number[]>} existing — id→vector
 * @returns {Promise<Object<string, number[]>>} — updated map
 */
export async function embedProfiles(profiles, existing = {}) {
  const eMap = { ...existing }
  for (const p of profiles) {
    if (!eMap[p.id]) {
      const input = `${p.skill} ${(p.tags || []).join(' ')}`
      eMap[p.id] = await embedText(input)
    }
  }
  return eMap
}

/**
 * Compute cosine similarity between two equal-length vectors.
 * @param {number[]} a
 * @param {number[]} b
 * @returns {number}
 */
export function cosineSimilarity(a, b) {
  let dot = 0, magA = 0, magB = 0
  for (let i = 0; i < a.length; i++) {
    dot  += a[i] * b[i]
    magA += a[i] * a[i]
    magB += b[i] * b[i]
  }
  return dot / (Math.sqrt(magA) * Math.sqrt(magB))
}

/**
 * Given all profiles and user goals, embeds & scores them.
 * @param {Array<Object>} profiles — profiles to match
 * @param {Object<string, number[]>} embeddingsMap — existing id→vector
 * @param {Array<string>} goals — user’s goal strings
 * @returns {Promise<{ matches: Array<Object>, embeddingsMap: Object }>}
 */
export async function computeMatches(profiles, embeddingsMap, goals) {
  // If no goals, return empty matches
  if (!goals.length) return { matches: [], embeddingsMap }

  // 1. Embed the concatenated goals
  const goalText = goals.join(' • ')
  const qVec = await embedText(goalText)

  // 2. Embed all profiles, reusing cache
  const eMap = await embedProfiles(profiles, embeddingsMap)

  // 3. Score each profile by cosine similarity, sort descending
  const scored = profiles
    .map(p => ({
      ...p,
      score: cosineSimilarity(qVec, eMap[p.id]),
    }))
    .sort((a, b) => b.score - a.score)

  return { matches: scored, embeddingsMap: eMap }
}
